import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, fieldError, toApiError } from '../api/problem';
import { useI18n } from '../i18n/I18nProvider';
import { useAuth } from '../auth/AuthContext';

const MIN_PASSWORD_LENGTH = 8;

export function RegisterPage() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    // Ô nhắc lại mật khẩu chỉ tồn tại trên giao diện, máy chủ không biết đến nó nên phải tự kiểm.
    if (password !== confirm) {
      setMismatch(true);
      return;
    }

    setMismatch(false);
    setBusy(true);

    try {
      await register(email.trim(), password, displayName.trim());
      navigate('/', { replace: true });
    } catch (caught) {
      setError(toApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-card">
      <h1 className="h4 mb-3">{t('auth.register.title')}</h1>

      {error && <div className="alert alert-danger py-2">{error.message}</div>}

      <form onSubmit={submit} noValidate>
        <div className="mb-3">
          <label className="form-label" htmlFor="register-name">
            {t('auth.displayName')}
          </label>
          <input
            id="register-name"
            className={`form-control ${fieldError(error, 'displayName') ? 'is-invalid' : ''}`}
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            required
          />
          <div className="invalid-feedback">{fieldError(error, 'displayName')}</div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="register-email">
            {t('auth.email')}
          </label>
          <input
            id="register-email"
            type="email"
            autoComplete="username"
            className={`form-control ${fieldError(error, 'email') ? 'is-invalid' : ''}`}
            value={email}
            onChange={event => setEmail(event.target.value)}
            required
          />
          <div className="invalid-feedback">{fieldError(error, 'email')}</div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="register-password">
            {t('auth.password')}
          </label>
          <input
            id="register-password"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            className={`form-control ${fieldError(error, 'password') ? 'is-invalid' : ''}`}
            value={password}
            onChange={event => setPassword(event.target.value)}
            required
          />
          <div className="form-text">{t('auth.passwordHint', { count: MIN_PASSWORD_LENGTH })}</div>
          <div className="invalid-feedback">{fieldError(error, 'password')}</div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="register-confirm">
            {t('auth.confirmPassword')}
          </label>
          <input
            id="register-confirm"
            type="password"
            autoComplete="new-password"
            className={`form-control ${mismatch ? 'is-invalid' : ''}`}
            value={confirm}
            onChange={event => setConfirm(event.target.value)}
            required
          />
          <div className="invalid-feedback">{t('auth.mismatch')}</div>
        </div>

        <button type="submit" className="btn btn-primary w-100" disabled={busy}>
          {busy ? t('auth.register.busy') : t('auth.register.submit')}
        </button>
      </form>

      <p className="text-body-secondary small mt-3 mb-0">
        {t('auth.haveAccount')} <Link to="/login">{t('auth.signIn.title')}</Link>
      </p>
    </div>
  );
}
